// A QR code, computed here, from ISO/IEC 18004. Byte mode, error correction
// level M, versions 1 to 16.
//
// WHY THIS IS WRITTEN OUT RATHER THAN INSTALLED OR FETCHED. The thing this draws
// is a TOTP shared secret, which is the one value in XP1 that is worth exactly
// as much as the account it belongs to, for as long as that account exists.
// api/src/auth/routes.ts calls the response carrying it "THE ONE RESPONSE IN XP1
// THAT CARRIES A SHARED SECRET". Two of the three obvious ways to turn a string
// into a QR code are ruled out by that sentence rather than by taste:
//
//   AN IMAGE SERVICE IS A REFUSAL, NOT A TRADE-OFF. Every hosted QR endpoint
//   takes its payload in the query string, so using one would put the account's
//   second factor in somebody else's access log, TLS-terminated by somebody else,
//   for ever. There is no size or effort argument on the other side of that. It
//   is a disclosure of the shared secret to an outsider and it is the reason
//   nothing in this file reaches the network.
//
//   A DEPENDENCY IS A JUDGEMENT, AND IT WENT THIS WAY. The encoders on npm are
//   larger than this file, carry their own release cadence, and would sit in the
//   bundle of the surface that shows a Nova admin every client's attendee data —
//   the surface index.html's policy is written around. The problem, unlike most
//   things one reaches for a library for, is closed: a finite specification with
//   no I/O, no configuration and no reason ever to change. So it is here, and
//   check.mjs holds it to an independent implementation module for module rather
//   than to my own reading of the specification.
//
// WHAT IS DELIBERATELY NOT HERE. Numeric and alphanumeric modes, which would
// make a shorter symbol for a payload that is neither; error correction levels
// other than M; and any decoder. Every one of those is code with no caller, and
// a QR encoder is exactly the kind of file that grows a mode nobody reads.
//
// ON LEVEL M. The trade is symbol size against how much of the printed or
// displayed code may be obscured. L would make a smaller symbol; M recovers
// about 15% and is what practically every authenticator provisioning code in the
// world uses, which matters because the failure it protects against here is a
// phone camera at an angle to a laptop screen with a reflection across it.

/**
 * The largest symbol this file will produce.
 *
 * Sixteen is not the specification's limit — it goes to 40 — it is where the
 * tables below stop, and they stop there because a version is forty lines of
 * table each and an unused one is forty lines nothing checks. Level M at version
 * 16 carries 442 bytes, and the longest provisioning URI this console can be
 * asked to draw is an issuer, a percent-encoded email address and five
 * parameters, which is under 300 even for an address at the RFC 5321 limit.
 *
 * A payload past this is refused by name rather than truncated, because a
 * truncated otpauth URI is a QR code that scans, produces an entry in somebody's
 * authenticator, and generates codes that will never work.
 */
export const QR_MAX_VERSION = 16;

/**
 * How a version's codewords are divided into Reed–Solomon blocks, at level M.
 *
 * Transcribed from the specification's table 13 to 22, and this is the part of
 * the file where a typo is invisible: a wrong data-codeword count produces a
 * symbol that is structurally perfect, scans, and decodes to rubbish. So it is
 * checked two ways that do not involve reading it again. check.mjs derives each
 * version's total codeword count from the *geometry* of the symbol — module
 * count minus function patterns, divided by eight — and compares; and it
 * compares whole matrices against a separate implementation.
 */
export type QrBlockPlan = {
  readonly eccPerBlock: number;
  /** Short blocks first, which is the order the interleave below depends on. */
  readonly groups: readonly { readonly blocks: number; readonly dataCodewords: number }[];
};

export const QR_BLOCK_PLAN: readonly (QrBlockPlan | null)[] = [
  null,
  { eccPerBlock: 10, groups: [{ blocks: 1, dataCodewords: 16 }] },
  { eccPerBlock: 16, groups: [{ blocks: 1, dataCodewords: 28 }] },
  { eccPerBlock: 26, groups: [{ blocks: 1, dataCodewords: 44 }] },
  { eccPerBlock: 18, groups: [{ blocks: 2, dataCodewords: 32 }] },
  { eccPerBlock: 24, groups: [{ blocks: 2, dataCodewords: 43 }] },
  { eccPerBlock: 16, groups: [{ blocks: 4, dataCodewords: 27 }] },
  { eccPerBlock: 18, groups: [{ blocks: 4, dataCodewords: 31 }] },
  { eccPerBlock: 22, groups: [{ blocks: 2, dataCodewords: 38 }, { blocks: 2, dataCodewords: 39 }] },
  { eccPerBlock: 22, groups: [{ blocks: 3, dataCodewords: 36 }, { blocks: 2, dataCodewords: 37 }] },
  { eccPerBlock: 26, groups: [{ blocks: 4, dataCodewords: 43 }, { blocks: 1, dataCodewords: 44 }] },
  { eccPerBlock: 30, groups: [{ blocks: 1, dataCodewords: 50 }, { blocks: 4, dataCodewords: 51 }] },
  { eccPerBlock: 22, groups: [{ blocks: 6, dataCodewords: 36 }, { blocks: 2, dataCodewords: 37 }] },
  { eccPerBlock: 22, groups: [{ blocks: 8, dataCodewords: 37 }, { blocks: 1, dataCodewords: 38 }] },
  { eccPerBlock: 24, groups: [{ blocks: 4, dataCodewords: 40 }, { blocks: 5, dataCodewords: 41 }] },
  { eccPerBlock: 24, groups: [{ blocks: 5, dataCodewords: 41 }, { blocks: 5, dataCodewords: 42 }] },
  { eccPerBlock: 28, groups: [{ blocks: 7, dataCodewords: 45 }, { blocks: 3, dataCodewords: 46 }] },
];

/**
 * Where the alignment patterns' centres sit, per version.
 *
 * The first is always 6, which is why the entry for a version with two of them
 * reads as a lopsided pair rather than a mistake: the pattern at 6 is the one
 * that shares a row with the timing pattern.
 */
const ALIGNMENT_CENTRES: readonly (readonly number[])[] = [
  [],
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
  [6, 26, 50, 74],
];

/** Byte mode. The four-bit indicator that opens the bit stream. */
const MODE_BYTE = 0b0100;

/**
 * Level M's two bits, as the format information spells them.
 *
 * Not in the numeric order anyone would guess — L is 01 and M is 00 — which is
 * why it is a named constant rather than an index into a list.
 */
const ECC_LEVEL_BITS = 0b00;

/** The two alternating bytes the specification pads a short message with. */
const PAD_CODEWORDS = [0xec, 0x11] as const;

/** Four light modules on every side, without which many readers will not attempt a scan. */
export const QR_QUIET_ZONE = 4;

export type QrMatrix = {
  /** 4 × version + 17, excluding the quiet zone. */
  readonly size: number;
  readonly version: number;
  /** Which of the eight masks was applied. */
  readonly mask: number;
  /** Row-major, one entry per module: 1 dark, 0 light. */
  readonly modules: Uint8Array;
};

export type QrResult =
  | { readonly ok: true; readonly matrix: QrMatrix }
  /**
   * Refused rather than approximated, and the caller draws the text instead.
   *
   * A QR code is a convenience on this screen and the base32 secret beside it is
   * the thing that actually enrols an authenticator, so there is a correct
   * degradation available. Producing a symbol that encodes part of a
   * provisioning URI would not be a degradation: it would scan, and it would
   * enrol an app against a secret that matches nothing.
   */
  | { readonly ok: false; readonly problem: string };

export function dataCodewordsFor(version: number): number {
  const plan = QR_BLOCK_PLAN[version];
  if (plan === undefined || plan === null) return 0;
  let total = 0;
  for (const group of plan.groups) total += group.blocks * group.dataCodewords;
  return total;
}

export function totalCodewordsFor(version: number): number {
  const plan = QR_BLOCK_PLAN[version];
  if (plan === undefined || plan === null) return 0;
  let blocks = 0;
  for (const group of plan.groups) blocks += group.blocks;
  return dataCodewordsFor(version) + blocks * plan.eccPerBlock;
}

/** Eight for a small symbol, sixteen from version 10, which changes where the data starts. */
function countIndicatorBits(version: number): number {
  return version < 10 ? 8 : 16;
}

/**
 * A QR code carrying exactly `text`.
 *
 * `mask` is accepted so that check.mjs can compare every one of the eight
 * against another implementation rather than only the one this function would
 * have chosen. Left out, the mask is chosen by the specification's penalty
 * rules, which is what a reader expects and what makes a symbol easiest to scan.
 */
export function encodeQr(text: string, mask?: number): QrResult {
  const bytes = new TextEncoder().encode(text);

  const version = smallestVersionFor(bytes.length);
  if (version === null) {
    return {
      ok: false,
      problem:
        `that is ${bytes.length} bytes and the largest symbol this console builds is version ` +
        `${QR_MAX_VERSION}, which holds ${dataCodewordsFor(QR_MAX_VERSION) - 3}. Nothing has been drawn ` +
        "rather than a code drawn from part of it.",
    };
  }

  if (mask !== undefined && (!Number.isInteger(mask) || mask < 0 || mask > 7)) {
    return { ok: false, problem: `there are eight masks, numbered 0 to 7, and ${mask} is not one of them` };
  }

  const codewords = interleave(version, dataStream(bytes, version));
  const size = 4 * version + 17;

  const reserved = new Uint8Array(size * size);
  const base = new Uint8Array(size * size);
  drawFunctionPatterns(base, reserved, size, version);
  placeData(base, reserved, size, codewords);

  let best: { modules: Uint8Array; mask: number; penalty: number } | null = null;

  for (let candidate = 0; candidate < 8; candidate += 1) {
    if (mask !== undefined && candidate !== mask) continue;

    const modules = base.slice();
    applyMask(modules, reserved, size, candidate);
    drawFormatInformation(modules, size, candidate);
    if (version >= 7) drawVersionInformation(modules, size, version);

    const penalty = penaltyScore(modules, size);
    // Strictly less than, so the lowest-numbered mask wins a tie. Which mask a
    // tie resolves to changes nothing about whether the symbol reads, and having
    // it be deterministic is what lets a check compare two runs.
    if (best === null || penalty < best.penalty) best = { modules, mask: candidate, penalty };
  }

  if (best === null) {
    // Unreachable: the loop above runs for at least one candidate whenever the
    // mask argument passed the range check. Named rather than asserted with a
    // `!`, because the alternative to a sentence here is a type assertion that
    // stops being true if somebody adds a ninth mask.
    return { ok: false, problem: "no mask was applied, which cannot happen and means this file has been edited wrongly" };
  }

  return { ok: true, matrix: { size, version, mask: best.mask, modules: best.modules } };
}

function smallestVersionFor(byteLength: number): number | null {
  for (let version = 1; version <= QR_MAX_VERSION; version += 1) {
    const capacityBits = dataCodewordsFor(version) * 8;
    if (4 + countIndicatorBits(version) + byteLength * 8 <= capacityBits) return version;
  }
  return null;
}

// ------------------------------------------------------------------ the bit stream

/**
 * The data codewords for one message: mode, length, bytes, terminator, padding.
 *
 * The padding is two stages and they are not interchangeable. Up to four zero
 * bits close the stream, then zeros to the next byte boundary, and only then the
 * alternating pad bytes. A reader that has consumed the declared length stops
 * looking, so what follows is never read — which is exactly why getting it wrong
 * produces a symbol that works on the reader you tested and fails on another.
 */
function dataStream(bytes: Uint8Array, version: number): Uint8Array {
  const capacity = dataCodewordsFor(version);
  const bits: number[] = [];

  const push = (value: number, width: number): void => {
    for (let shift = width - 1; shift >= 0; shift -= 1) bits.push((value >>> shift) & 1);
  };

  push(MODE_BYTE, 4);
  push(bytes.length, countIndicatorBits(version));
  for (const byte of bytes) push(byte, 8);

  const terminator = Math.min(4, capacity * 8 - bits.length);
  push(0, terminator);
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords = new Uint8Array(capacity);
  for (let index = 0; index < bits.length; index += 8) {
    let byte = 0;
    for (let offset = 0; offset < 8; offset += 1) byte = (byte << 1) | bits[index + offset]!;
    codewords[index / 8] = byte;
  }

  for (let index = bits.length / 8; index < capacity; index += 1) {
    codewords[index] = PAD_CODEWORDS[(index - bits.length / 8) % 2]!;
  }

  return codewords;
}

// ------------------------------------------------------- Reed–Solomon over GF(256)

/**
 * The two logarithm tables for the field, built once.
 *
 * 0x11d is the primitive polynomial the specification names, and it is the one
 * number in this file that cannot be got wrong quietly: a different polynomial
 * produces parity bytes that are a valid codeword of a different code, so every
 * symbol would be structurally perfect and unreadable by everything.
 */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

{
  let value = 1;
  for (let index = 0; index < 255; index += 1) {
    EXP[index] = value;
    LOG[value] = index;
    value <<= 1;
    if (value & 0x100) value ^= 0x11d;
  }
  for (let index = 255; index < 512; index += 1) EXP[index] = EXP[index - 255]!;
}

const multiply = (a: number, b: number): number =>
  a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!;

/**
 * The generator polynomial for `degree` parity bytes: (x - α⁰)(x - α¹)…
 *
 * Built rather than tabulated, because the tabulated form is thirty rows of
 * bytes that no reader can check and that a check comparing this file to itself
 * would happily confirm.
 */
function generatorPolynomial(degree: number): Uint8Array {
  let polynomial = new Uint8Array([1]);

  for (let index = 0; index < degree; index += 1) {
    const next = new Uint8Array(polynomial.length + 1);
    for (let term = 0; term < polynomial.length; term += 1) {
      next[term] = next[term]! ^ polynomial[term]!;
      next[term + 1] = next[term + 1]! ^ multiply(polynomial[term]!, EXP[index]!);
    }
    polynomial = next;
  }

  return polynomial;
}

/** The parity bytes for one block: the remainder of the data divided by the generator. */
function parityFor(data: Uint8Array, count: number): Uint8Array {
  const generator = generatorPolynomial(count);
  const remainder = new Uint8Array(count);

  for (const byte of data) {
    const factor = byte ^ remainder[0]!;
    remainder.copyWithin(0, 1);
    remainder[count - 1] = 0;
    for (let index = 0; index < count; index += 1) {
      remainder[index] = remainder[index]! ^ multiply(generator[index + 1]!, factor);
    }
  }

  return remainder;
}

/**
 * The blocks' bytes woven together, which is what makes the error correction
 * worth having.
 *
 * A burst of damage across a symbol — a thumb, a glare, a fold — hits
 * consecutive modules, which is consecutive codewords. Storing each block's
 * bytes together would put that whole burst inside one block and past what its
 * parity can repair, while every other block sat undamaged. Interleaving spreads
 * one burst across every block, so each has a few errors rather than one having
 * all of them.
 */
function interleave(version: number, data: Uint8Array): Uint8Array {
  const plan = QR_BLOCK_PLAN[version];
  if (plan === undefined || plan === null) return new Uint8Array(0);

  const blocks: { data: Uint8Array; parity: Uint8Array }[] = [];
  let read = 0;

  for (const group of plan.groups) {
    for (let index = 0; index < group.blocks; index += 1) {
      const slice = data.subarray(read, read + group.dataCodewords);
      read += group.dataCodewords;
      blocks.push({ data: slice, parity: parityFor(slice, plan.eccPerBlock) });
    }
  }

  const out: number[] = [];
  const longest = Math.max(...blocks.map((block) => block.data.length));

  for (let column = 0; column < longest; column += 1) {
    for (const block of blocks) {
      const byte = block.data[column];
      if (byte !== undefined) out.push(byte);
    }
  }
  for (let column = 0; column < plan.eccPerBlock; column += 1) {
    for (const block of blocks) out.push(block.parity[column]!);
  }

  return new Uint8Array(out);
}

// ------------------------------------------------------------------ the modules

const setFunction = (
  modules: Uint8Array,
  reserved: Uint8Array,
  size: number,
  row: number,
  column: number,
  dark: boolean,
): void => {
  modules[row * size + column] = dark ? 1 : 0;
  reserved[row * size + column] = 1;
};

/**
 * Everything a reader locates the symbol by, before any data goes in.
 *
 * The format information's own modules are written here with zeros and marked
 * reserved, then written again for real once a mask has been chosen. Two passes,
 * because the data placement below has to know those modules are not available
 * and the mask is not known until the data is in.
 */
function drawFunctionPatterns(
  modules: Uint8Array,
  reserved: Uint8Array,
  size: number,
  version: number,
): void {
  for (const [row, column] of [[0, 0], [0, size - 7], [size - 7, 0]] as const) {
    for (let dr = -1; dr <= 7; dr += 1) {
      for (let dc = -1; dc <= 7; dc += 1) {
        const r = row + dr;
        const c = column + dc;
        if (r < 0 || r >= size || c < 0 || c >= size) continue;
        // The separator is the ring of light modules the loop's -1 and 7 reach,
        // and it is not decoration: without it a finder pattern touching data
        // stops matching the 1:1:3:1:1 ratio a reader scans for.
        const ring = Math.max(Math.abs(dr - 3), Math.abs(dc - 3));
        setFunction(modules, reserved, size, r, c, ring !== 2 && ring <= 3);
      }
    }
  }

  for (let index = 8; index < size - 8; index += 1) {
    const dark = index % 2 === 0;
    setFunction(modules, reserved, size, 6, index, dark);
    setFunction(modules, reserved, size, index, 6, dark);
  }

  const centres = ALIGNMENT_CENTRES[version] ?? [];
  for (const row of centres) {
    for (const column of centres) {
      // The three that would sit on a finder pattern are skipped rather than
      // drawn and overwritten, because drawing them would clear separator
      // modules that the finder needs.
      const onFinder =
        (row === centres[0] && column === centres[0]) ||
        (row === centres[0] && column === centres[centres.length - 1]) ||
        (row === centres[centres.length - 1] && column === centres[0]);
      if (onFinder) continue;

      for (let dr = -2; dr <= 2; dr += 1) {
        for (let dc = -2; dc <= 2; dc += 1) {
          setFunction(modules, reserved, size, row + dr, column + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
        }
      }
    }
  }

  for (let index = 0; index < 15; index += 1) {
    reserveFormatModule(modules, reserved, size, index, false);
  }
  // Always dark, whatever the format bits are, and it is the one module a reader
  // uses to tell a QR code from something that merely looks like one.
  setFunction(modules, reserved, size, size - 8, 8, true);

  if (version >= 7) {
    for (let index = 0; index < 18; index += 1) {
      const along = size - 11 + (index % 3);
      const across = Math.floor(index / 3);
      setFunction(modules, reserved, size, across, along, false);
      setFunction(modules, reserved, size, along, across, false);
    }
  }
}

/**
 * Where one of the fifteen format bits lives, in both of its copies.
 *
 * Two copies at opposite corners, so a symbol with one corner destroyed is still
 * readable — which is the whole reason the format information is not simply
 * beside the data it describes. The positions skip row and column 6 because the
 * timing patterns are there, which is why this is a sequence of cases rather
 * than arithmetic.
 */
function reserveFormatModule(
  modules: Uint8Array,
  reserved: Uint8Array,
  size: number,
  index: number,
  dark: boolean,
): void {
  // The first copy turns the corner at row 8: bits 0 to 7 come down column 8,
  // then bits 8 to 14 run left along row 8. Both legs step over row and column
  // 6, which is why index 6 and index 8 are cases of their own rather than
  // arithmetic — and writing the timing pattern's own module here instead of the
  // one beside it is the mistake this shape exists to make visible.
  if (index < 6) setFunction(modules, reserved, size, index, 8, dark);
  else if (index === 6) setFunction(modules, reserved, size, 7, 8, dark);
  else if (index === 7) setFunction(modules, reserved, size, 8, 8, dark);
  else if (index === 8) setFunction(modules, reserved, size, 8, 7, dark);
  else setFunction(modules, reserved, size, 8, 14 - index, dark);

  // The second copy is split across two corners rather than turning one, so its
  // legs are eight and seven rather than eight and seven the other way round:
  // bits 0 to 7 run right along row 8 from column size-8, and bits 8 to 14 down
  // column 8 from row size-7. The module at (size-8, 8) belongs to neither — it
  // is the one that is always dark — which is what makes the two legs unequal.
  if (index < 8) setFunction(modules, reserved, size, 8, size - 1 - index, dark);
  else setFunction(modules, reserved, size, size - 15 + index, 8, dark);
}

/**
 * The data, in the two-module-wide upward-then-downward snake the specification
 * defines.
 *
 * Column 6 is stepped over rather than skipped inside the loop, because it is a
 * whole column of timing pattern: treating it as a column with no free modules
 * would shift every pair after it by one and produce a symbol that decodes to
 * nothing.
 */
function placeData(modules: Uint8Array, reserved: Uint8Array, size: number, codewords: Uint8Array): void {
  let bit = 0;
  const total = codewords.length * 8;

  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;

    for (let step = 0; step < size; step += 1) {
      for (let offset = 0; offset < 2; offset += 1) {
        const column = right - offset;
        const upward = ((right + 1) & 2) === 0;
        const row = upward ? size - 1 - step : step;

        if (reserved[row * size + column] === 1 || bit >= total) continue;

        modules[row * size + column] = (codewords[bit >>> 3]! >>> (7 - (bit & 7))) & 1;
        bit += 1;
      }
    }
  }
}

function applyMask(modules: Uint8Array, reserved: Uint8Array, size: number, mask: number): void {
  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column < size; column += 1) {
      if (reserved[row * size + column] === 1) continue;
      const at = row * size + column;
      if (maskAt(mask, row, column)) modules[at] = modules[at]! ^ 1;
    }
  }
}

function maskAt(mask: number, row: number, column: number): boolean {
  switch (mask) {
    case 0:
      return (row + column) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return column % 3 === 0;
    case 3:
      return (row + column) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(column / 3)) % 2 === 0;
    case 5:
      return ((row * column) % 2) + ((row * column) % 3) === 0;
    case 6:
      return (((row * column) % 2) + ((row * column) % 3)) % 2 === 0;
    default:
      return ((((row + column) % 2) + ((row * column) % 3)) % 2) === 0;
  }
}

/**
 * The fifteen format bits: five of content and ten of BCH parity, then a fixed
 * mask.
 *
 * The final exclusive-or with 0x5412 is what stops level M with mask 0 — five
 * zero bits, whose BCH parity is also zero — from producing fifteen light
 * modules where a reader expects to find the one thing it reads before anything
 * else.
 */
function drawFormatInformation(modules: Uint8Array, size: number, mask: number): void {
  const content = (ECC_LEVEL_BITS << 3) | mask;
  let remainder = content;
  for (let index = 0; index < 10; index += 1) {
    remainder = (remainder << 1) ^ ((remainder >>> 9) * 0b10100110111);
  }
  const bits = (((content << 10) | remainder) ^ 0b101010000010010) & 0x7fff;

  const spare = new Uint8Array(size * size);
  for (let index = 0; index < 15; index += 1) {
    reserveFormatModule(modules, spare, size, index, ((bits >>> index) & 1) === 1);
  }
}

/** Six version bits and twelve of BCH parity, in two copies, from version 7 up. */
function drawVersionInformation(modules: Uint8Array, size: number, version: number): void {
  let remainder = version;
  for (let index = 0; index < 12; index += 1) {
    remainder = (remainder << 1) ^ ((remainder >>> 11) * 0b1111100100101);
  }
  const bits = ((version << 12) | remainder) & 0x3ffff;

  for (let index = 0; index < 18; index += 1) {
    const dark = ((bits >>> index) & 1) === 1 ? 1 : 0;
    const along = size - 11 + (index % 3);
    const across = Math.floor(index / 3);
    modules[across * size + along] = dark;
    modules[along * size + across] = dark;
  }
}

/**
 * How hard a masked symbol is to read, by the specification's four rules, kept
 * apart rather than summed.
 *
 * The point of masking is not aesthetic. An unmasked symbol can contain long
 * uniform runs, large blocks of one colour, and sequences that look like a finder
 * pattern — all of which make a reader locate the symbol wrongly or not at all.
 * Every one of the eight masks is tried and the least penalised is kept, because
 * which mask is best depends entirely on the payload.
 *
 * THE FOUR ARE RETURNED SEPARATELY SO THAT A CHECK CAN DISAGREE WITH ONE OF
 * THEM. A single number can only be compared against another implementation's
 * single number, and when the two differ there is nothing to say about which
 * rule is wrong — which is exactly the position this file was in when the mask it
 * chose for one payload differed from the reference implementation's and the
 * matrices were identical under every forced mask. Four numbers turn that into an
 * answerable question.
 */
export type QrPenalties = {
  /** Runs of five or more of one colour, along every row and column. */
  readonly runs: number;
  /** Two-by-two blocks of one colour. */
  readonly blocks: number;
  /** Sequences that a reader could mistake for a finder pattern. */
  readonly finderLike: number;
  /** How far the proportion of dark modules is from half. */
  readonly balance: number;
};

export function qrPenalties(modules: Uint8Array, size: number): QrPenalties {
  const at = (row: number, column: number): number => modules[row * size + column]!;
  const lines: ((index: number) => number)[] = [];
  for (let line = 0; line < size; line += 1) {
    const fixed = line;
    lines.push((index) => at(fixed, index));
    lines.push((index) => at(index, fixed));
  }

  let runs = 0;
  for (const read of lines) {
    let run = 1;
    for (let index = 1; index < size; index += 1) {
      if (read(index) === read(index - 1)) {
        run += 1;
        continue;
      }
      if (run >= 5) runs += 3 + (run - 5);
      run = 1;
    }
    if (run >= 5) runs += 3 + (run - 5);
  }

  let blocks = 0;
  for (let row = 0; row < size - 1; row += 1) {
    for (let column = 0; column < size - 1; column += 1) {
      const corner = at(row, column);
      if (corner === at(row, column + 1) && corner === at(row + 1, column) && corner === at(row + 1, column + 1)) {
        blocks += 3;
      }
    }
  }

  // The 1:1:3:1:1 finder ratio with four light modules on one side of it. Counted
  // in both directions, and a sequence with four light modules on BOTH sides is
  // counted twice rather than once — which is what the specification's wording
  // gives and is the detail implementations differ on.
  const FINDER = [1, 0, 1, 1, 1, 0, 1] as const;
  let finderLike = 0;
  for (const read of lines) {
    for (let start = 0; start + 11 <= size; start += 1) {
      let leadingGap = true;
      let trailingGap = true;
      for (let offset = 0; offset < 4; offset += 1) {
        if (read(start + offset) !== 0) leadingGap = false;
        if (read(start + 7 + offset) !== 0) trailingGap = false;
      }
      let after = true;
      let before = true;
      for (let offset = 0; offset < 7; offset += 1) {
        if (read(start + 4 + offset) !== FINDER[offset]) after = false;
        if (read(start + offset) !== FINDER[offset]) before = false;
      }
      if (leadingGap && after) finderLike += 40;
      if (before && trailingGap) finderLike += 40;
    }
  }

  let dark = 0;
  for (const module of modules) dark += module;
  const total = size * size;
  // Every complete five percentage points of drift from half costs ten, so a
  // symbol anywhere in 45% to 55% costs nothing. Written as integers throughout
  // rather than as a percentage, because the percentage form puts a division
  // before a floor and lands a symbol sitting exactly on a boundary on whichever
  // side the floating point happened to fall.
  const balance = 10 * Math.floor(Math.abs(dark * 200 - total * 100) / (total * 10));

  return { runs, blocks, finderLike, balance };
}

function penaltyScore(modules: Uint8Array, size: number): number {
  const penalties = qrPenalties(modules, size);
  return penalties.runs + penalties.blocks + penalties.finderLike + penalties.balance;
}
